import { Capacitor, registerPlugin } from '@capacitor/core'

import { encolarPosicion, subirPosicionesPendientes } from '@/lib/campo/colaDePosiciones'
import { guardarUltimo, leerUltimo } from '@/lib/campo/loUltimoQueSeSupo'
import { supabase } from '@/lib/supabase/cliente'
import type { Geocerca } from '@/lib/supabase/tipos-bd'

/**
 * EL RASTREO EN SEGUNDO PLANO, EL QUE EL NAVEGADOR NO PUEDE.
 *
 * La versión web (src/modules/ejecucion/useRastreo.ts) solo vigila mientras la
 * pantalla está encendida y la pestaña viva: en cuanto el técnico bloquea el
 * teléfono, deja de reportar. Este módulo hace lo mismo pero con un SERVICIO
 * EN PRIMER PLANO de Android, que sigue vivo con la pantalla apagada, y por eso
 * las llegadas y salidas se registran aunque el técnico guarde el teléfono.
 *
 * La regla NO cambia de sitio: sigue en la base. El teléfono recibe sus
 * geocercas de fn_mis_geocercas y solo habla al cruzar un borde, más un latido
 * cada diez minutos. Quién decide qué significa cada cruce sigue siendo
 * fn_reportar_posicion, ahora con origen 'android'. Es exactamente el mismo
 * contrato que la web, para que el tablero no note de dónde vino el punto.
 *
 * Solo corre dentro de la app nativa de Android. En el navegador este archivo
 * se importa pero nunca se ejecuta (la guarda de isNativePlatform lo apaga), y
 * el plugin ni siquiera se toca, así que la web sigue igual.
 */

/** Latido cada diez minutos, igual que la web, para la alarma de señal perdida. */
const LATIDO_MS = 10 * 60 * 1000

/** Una lectura del servicio de ubicación de Android. */
type Ubicacion = {
  latitude: number
  longitude: number
  accuracy: number | null
  // El plugin puede mandar más campos; solo se usan estos.
}

/** El plugin nativo. Se registra por nombre para no importar JS que asuma Android. */
type PluginRastreo = {
  addWatcher(
    opciones: {
      backgroundMessage?: string
      backgroundTitle?: string
      requestPermissions?: boolean
      stale?: boolean
      distanceFilter?: number
    },
    callback: (posicion?: Ubicacion, error?: { code?: string; message?: string }) => void,
  ): Promise<string>
  removeWatcher(opciones: { id: string }): Promise<void>
  openSettings(): Promise<void>
}

const Rastreo = registerPlugin<PluginRastreo>('BackgroundGeolocation')

/** Metros entre dos puntos: la misma cuenta que hace la base, para decidir aquí. */
function distanciaM(lat1: number, lon1: number, lat2: number, lon2: number): number {
  const rad = Math.PI / 180
  const a =
    Math.sin(((lat2 - lat1) * rad) / 2) ** 2 +
    Math.cos(lat1 * rad) * Math.cos(lat2 * rad) * Math.sin(((lon2 - lon1) * rad) / 2) ** 2

  return 6_371_000 * 2 * Math.asin(Math.sqrt(a))
}

let idVigilancia: string | null = null
let ultimoLatido = 0
/** La última lectura que entregó el servicio, para poder latir estando quieto. */
let ultimaPosicion: Ubicacion | null = null
let relojLatido: ReturnType<typeof setInterval> | null = null
/** En cuáles áreas cree el teléfono que está. Solo habla cuando esto cambia. */
const adentro = new Set<string>()
let cercas: Geocerca[] = []

/**
 * Trae las geocercas del técnico, tal como las define la base, Y LAS GUARDA.
 *
 * Sin guardarlas, arrancar la app sin señal dejaba `cercas` vacío: el teléfono
 * no sabía dónde estaban las obras, así que no detectaba ni una entrada ni una
 * salida en toda la jornada. Y es justamente en la obra sin cobertura donde
 * hace falta. Son cuatro números por orden y no cambian durante el día.
 */
async function traerGeocercas(): Promise<void> {
  const { data, error } = await supabase.rpc('fn_mis_geocercas')

  if (error) {
    // Lo último que se supo. Puede estar viejo —una orden reasignada no se
    // vería—, pero vigilar las obras de esta mañana es infinitamente mejor
    // que no vigilar ninguna.
    cercas = leerUltimo<Geocerca[]>('mis-geocercas') ?? []
    for (const cerca of cercas) {
      if (cerca.en_sitio) adentro.add(cerca.orden_trabajo_id)
    }
    return
  }

  cercas = ((data ?? []) as unknown as Geocerca[]).map((g) => ({
    ...g,
    latitud: Number(g.latitud),
    longitud: Number(g.longitud),
  }))

  guardarUltimo('mis-geocercas', cercas)

  // Punto de partida: si la base ya lo tiene adentro de un área, no anunciar otra
  // entrada. Igual que la web.
  for (const cerca of cercas) {
    if (cerca.en_sitio) adentro.add(cerca.orden_trabajo_id)
  }
}

/**
 * Manda el punto a la base, con la hora en que se tomó.
 *
 * Antes de hablar sube lo que estuviera esperando: la base lee la última
 * visita de cada orden para decidir qué significa un punto, así que un cruce
 * guardado tiene que entrar ANTES que el de ahora o se juzgaría al revés.
 *
 * Sin señal no se pierde: se guarda con su hora y sube cuando la haya. La
 * hora es lo único que no se puede reconstruir después, y es la que la base
 * usa para sumar las horas trabajadas.
 */
async function reportar(pos: Ubicacion, latido: boolean, cuando: string): Promise<void> {
  if (typeof navigator !== 'undefined' && !navigator.onLine) {
    encolarPosicion({
      latitud: pos.latitude,
      longitud: pos.longitude,
      exactitud: pos.accuracy,
      esLatido: latido,
      ocurridoEn: cuando,
    })
    return
  }

  try {
    await subirPosicionesPendientes()
  } catch {
    /* si lo guardado no pudo subir, lo de ahora se intenta igual */
  }

  const { error } = await supabase.rpc('fn_reportar_posicion', {
    p_latitud: pos.latitude,
    p_longitud: pos.longitude,
    p_exactitud_m: pos.accuracy ?? 9999,
    p_bateria: null,
    p_origen: 'android',
    p_es_latido: latido,
    p_ocurrido_en: cuando,
  })

  // Un fallo del servidor también se guarda: el cruce ya pasó y no vuelve.
  if (error) {
    encolarPosicion({
      latitud: pos.latitude,
      longitud: pos.longitude,
      exactitud: pos.accuracy,
      esLatido: latido,
      ocurridoEn: cuando,
    })
  }
}

/**
 * Decide si esta lectura merece hablar: al cruzar el borde de alguna geocerca,
 * o si ya pasaron diez minutos desde el último latido. Idéntico a la web.
 */
async function procesar(pos: Ubicacion): Promise<void> {
  ultimaPosicion = pos
  let cruce = false

  for (const cerca of cercas) {
    const d = distanciaM(pos.latitude, pos.longitude, cerca.latitud, cerca.longitud)
    const estaba = adentro.has(cerca.orden_trabajo_id)
    const entra = d <= cerca.radio_m
    const sale = d > cerca.radio_salida_m

    if (entra && !estaba) {
      adentro.add(cerca.orden_trabajo_id)
      cruce = true
    } else if (sale && estaba) {
      adentro.delete(cerca.orden_trabajo_id)
      cruce = true
    }
  }

  const ahora = Date.now()
  const latido = ahora - ultimoLatido >= LATIDO_MS

  if (!cruce && !latido) return

  /*
   * EL CRUCE NO SE PIERDE AUNQUE NO SE PUEDA AVISAR.
   *
   * Antes se marcaba `adentro`, se hablaba, y si la llamada fallaba —sin
   * señal, que es lo normal en obra— el cruce se perdía PARA SIEMPRE: el
   * teléfono ya creía haberlo contado y en la siguiente lectura ya no había
   * borde que cruzar. Una salida que no se reporta es una orden que se queda
   * abierta y unas horas que nadie suma.
   *
   * Ahora `reportar` lo guarda con su hora y lo sube cuando haya señal, así
   * que el apunte local puede quedarse como está: refleja la verdad —el
   * técnico salió— aunque la base todavía no lo sepa.
   */
  const antes = new Set(adentro)
  const latidoAntes = ultimoLatido

  ultimoLatido = ahora

  try {
    await reportar(pos, latido && !cruce, new Date().toISOString())
  } catch {
    // Ni guardar se pudo: se deshace para reintentarlo en la siguiente.
    adentro.clear()
    for (const id of antes) adentro.add(id)
    ultimoLatido = latidoAntes
  }
}

/**
 * Arranca el servicio en primer plano. Idempotente: si ya está corriendo, no
 * abre otro. Solo hace algo en la app nativa de Android.
 */
export async function iniciarRastreoAndroid(): Promise<void> {
  if (!Capacitor.isNativePlatform() || Capacitor.getPlatform() !== 'android') return
  if (idVigilancia) return

  await traerGeocercas()

  idVigilancia = await Rastreo.addWatcher(
    {
      backgroundTitle: 'MEGA está registrando tu jornada',
      backgroundMessage: 'Tu ubicación se usa para marcar llegadas y salidas.',
      requestPermissions: true,
      // Que no entregue puntos viejos guardados: solo lo que pasa ahora.
      stale: false,
      // SIN FILTRO DE DISTANCIA, y no es un descuido.
      //
      // Con `distanceFilter: 15` el servicio solo entregaba una lectura cuando
      // el técnico se movía quince metros. Estando quieto —que es lo que hace
      // justamente cuando está trabajando en un sitio— no entregaba nada, el
      // latido nunca salía y la oficina recibía "dejó de recibir su ubicación"
      // con el técnico ahí parado. Se midió: una sola posición en treinta horas.
      //
      // El ahorro no estaba aquí: el GPS ya está encendido por el servicio en
      // primer plano. Quien decide qué se reporta es `procesar`, que solo habla
      // al cruzar una geocerca o cada diez minutos.
      distanceFilter: 0,
    },
    (posicion, error) => {
      if (error || !posicion) return
      void procesar(posicion)
    },
  )

  // Red de seguridad: si el servicio se queda callado —sin señal, sin moverse,
  // o porque Android lo espació— el latido sale igual con la última posición
  // conocida. Sin esto, el silencio del teléfono se lee en la oficina como que
  // el técnico desapareció.
  if (!relojLatido) {
    relojLatido = setInterval(() => {
      if (!ultimaPosicion) return
      if (Date.now() - ultimoLatido < LATIDO_MS) return
      const latidoAntes = ultimoLatido
      ultimoLatido = Date.now()
      // Igual que arriba: si ni se pudo guardar, no cuenta como latido dado.
      void reportar(ultimaPosicion, true, new Date().toISOString()).catch(() => {
        ultimoLatido = latidoAntes
      })
    }, 60 * 1000)
  }
}

/** Detiene el servicio. Se usa al cerrar sesión. */
export async function detenerRastreoAndroid(): Promise<void> {
  if (!idVigilancia) return
  const id = idVigilancia
  idVigilancia = null
  adentro.clear()
  ultimoLatido = 0
  ultimaPosicion = null
  cercas = []
  if (relojLatido) {
    clearInterval(relojLatido)
    relojLatido = null
  }
  try {
    await Rastreo.removeWatcher({ id })
  } catch {
    /* si ya no existe, no importa */
  }
}

/** Abre los ajustes de la app, para que el técnico dé el permiso "Permitir siempre". */
export async function abrirAjustesUbicacion(): Promise<void> {
  if (!Capacitor.isNativePlatform()) return
  await Rastreo.openSettings()
}

/** ¿Estamos dentro de la app nativa? Para que la interfaz decida qué mostrar. */
export function esAppNativa(): boolean {
  return Capacitor.isNativePlatform()
}
